import { ExternalLink } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { formatFortalezaDate, formatNumber } from "@/utils/format";
import type { InstagramProductionItem, ProductionGalleryKind } from "@/services/marketing";
import { InstagramRemoteImage } from "./instagramImage";

const TITLES: Record<ProductionGalleryKind, string> = {
  feed: "Posts no Feed",
  reels: "Reels publicados",
  stories: "Stories",
};

interface Props {
  open: boolean;
  kind: ProductionGalleryKind | null;
  items: InstagramProductionItem[];
  onClose: () => void;
}

export function MarketingProductionGalleryModal({ open, kind, items, onClose }: Props) {
  const title = kind ? TITLES[kind] : "Publicações";

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnEscape
      closeOnBackdropClick
      size="xl"
      title={`${title} (${items.length})`}
    >
      {items.length > 0 && kind === "stories" && (
        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          A Meta só deixa ver stories ativos (~24h). O SEMOG arquiva cada story uma vez
          (sem duplicar) a cada 2 horas e guarda a mídia localmente. Stories publicados
          antes do início dessa coleta não voltam pela API.
        </p>
      )}
      {items.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-500">
          {kind === "stories" ? (
            <>
              Nenhum story disponível na API para este período.
              <br />
              Stories expiram em 24h — informe a quantidade manualmente em Definir metas.
            </>
          ) : (
            <>Nenhuma publicação encontrada no período.</>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((item) => (
            <article
              key={item.id}
              className="group overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-slate-200">
                <InstagramRemoteImage
                  url={item.thumbnailUrl}
                  alt=""
                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  fallback={
                    <div className="flex h-full items-center justify-center text-xs text-slate-500">
                      {item.mediaType}
                    </div>
                  }
                />
                {item.permalink && (
                  <a
                    href={item.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute right-2 top-2 rounded-full bg-black/50 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    aria-label="Abrir no Instagram"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
              <div className="space-y-1 p-2.5">
                <p className="line-clamp-2 text-[11px] text-slate-700">{item.caption}</p>
                <p className="text-[10px] text-slate-400">
                  {formatFortalezaDate(item.timestamp)}
                </p>
                <p className="text-[10px] text-slate-500">
                  {formatNumber(item.likes)} curtidas · {formatNumber(item.comments)} coment.
                </p>
              </div>
            </article>
          ))}
        </div>
      )}
    </Modal>
  );
}
